import { useEffect, useRef } from "react";
import { LINKS } from "../homeLinks";
import { SmartLink } from "./SmartLink";
import { HeroProductMarks } from "./HeroProductMarks";

/* Entrance on load (home.css): the headline, tagline, copy and buttons rise in one after
   another (.hero-rise, staggered by --i), while the photo settles from a slight zoom
   (.hero-photo). Pure CSS keyframes, so nothing here changes layout or scrolling. */
/* Parallax: the photo wrapper is pushed down by PARALLAX of the scroll distance while the hero
   is on screen, so the picture drifts at a third of the page speed. rAF-throttled, transform only
   (composited), and it stops updating once the hero has left the viewport. The gap this opens at
   the top of the hero is always off screen because the shift is smaller than the scroll. */
const PARALLAX = 0.3;

function useHeroParallax() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    let frame = 0;
    const update = () => {
      frame = 0;
      const y = window.scrollY;
      const limit = el.parentElement ? el.parentElement.offsetHeight : 0;
      el.style.setProperty("--hero-shift", `${Math.min(y, limit) * PARALLAX}px`);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);
  return ref;
}

/* Copy and layout from the v0 branch's 2026-09-26/27 revision: new headline and supporting copy,
   "Start for free", and the product lockups (HeroProductMarks) beside the buttons. */
export function HomeHero() {
  const photoRef = useHeroParallax();
  return (
    /* Full viewport so the fixed transparent header sits directly on the photo. */
    <section id="home-hero" className="relative isolate flex min-h-[calc(100svh+5rem)] flex-col overflow-hidden bg-black">
      <div ref={photoRef} className="hero-parallax absolute inset-0 -z-10">
        <img
          src="/images/home-hero-fleet.webp"
          alt="Renters meeting their booked vehicles on a city street at golden hour"
          fetchpriority="high"
          className="hero-photo h-full w-full object-cover object-center"
        />
      </div>

      {/* The photo's lower half is bright pavement, so weight the scrim toward
          the bottom where the headline and CTAs sit. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 bg-gradient-to-b from-black/25 via-black/20 to-black/60"
      />

      {/* Content is bottom-anchored (items-end). The bottom padding is the gap
          between the CTAs and the showcase panel below (minus its -mt overlap).
          On landscape desktops .hero-content (home.css) instead pins the headline so its
          top meets the bottom of the black car in the photo (owner, 2026-10-01). */}
      <div className="hero-content mx-auto flex w-full max-w-7xl flex-1 items-end px-5 pb-28 pt-32 sm:px-8 sm:pb-32 md:px-16">
        <div className="w-full">
          <div className="max-w-3xl">
            <h1 className="hero-rise font-sans text-5xl font-light leading-[0.95] tracking-tight text-white text-balance sm:text-6xl md:text-7xl lg:text-[72px]">
              Everything to do anything rental, all in one place
            </h1>

            <p style={{ "--i": 1 }} className="hero-rise mt-6 font-sans text-[22px] font-normal leading-snug text-white text-balance sm:text-2xl">
              Build fast. Start renting the same day.
            </p>

            <p style={{ "--i": 2 }} className="hero-rise mt-5 max-w-lg font-sans text-lg leading-relaxed text-white/85 text-pretty sm:text-xl">
              Run your branded rental business on an automated commerce platform, built for modern operators.
            </p>
          </div>

          <div style={{ "--i": 3 }} className="hero-rise mt-9 flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
              <SmartLink
                href={LINKS.getStarted}
                className="hero-cta-glow inline-flex items-center justify-center rounded-full px-8 py-4 text-base font-semibold text-black hover:bg-[#2be0a8] active:scale-95 sm:py-3.5"
                style={{ backgroundColor: "#33F7BE" }}
              >
                Start for free
              </SmartLink>
              {/* Owner (2026-10-01): "Book Demo" here instead of "What is Rentid". */}
              <SmartLink
                href={LINKS.bookDemo}
                newTab
                /* Padding is 1px under the primary so the 1px border lands this pill
                   at exactly the same height as the primary. */
                className="inline-flex items-center justify-center rounded-full border border-white/70 px-8 py-[15px] text-base font-semibold text-white transition-colors hover:bg-white/10 active:scale-95 sm:py-[13px]"
              >
                Book Demo
              </SmartLink>
            </div>
            {/* Owner (2026-10-05): the product logos share the buttons' line on desktop (right-aligned);
                below lg they stack under the buttons. */}
            <HeroProductMarks />
          </div>
        </div>
      </div>
    </section>
  );
}
